import type { ModelRef, Ref } from 'vue'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import type { UseFocusTrapOptions } from '@vueuse/integrations/useFocusTrap'
import type { TransitionVariant } from '~/components/_horizon/00.atoms/transition/types'

import type {
  ModalCloseTrigger,
  ModalDrawerPosition,
  ModalMobileType,
  ModalPosition,
  ModalSize,
} from './types'

type UseModalOptions = {
  mobileType?: ModalMobileType
  positionModal?: ModalPosition
  positionDrawer?: ModalDrawerPosition
  size?: Ref<ModalSize>
  enableOutsideDismiss?: boolean
  enableEscapeKey?: boolean
  enableScrollLock?: boolean
  visible: ModelRef<boolean>
  onClose?: (trigger: ModalCloseTrigger) => void
  modalRef: ReturnType<typeof useTemplateRef<HTMLDivElement>>
  focusTrapOptions?: UseFocusTrapOptions
}

export function useModal(options: UseModalOptions) {
  const {
    mobileType = 'drawer',
    positionModal = 'center',
    positionDrawer = 'bottom',
    enableOutsideDismiss = false,
    enableEscapeKey = false,
    enableScrollLock = false,
    visible,
    onClose,
    modalRef,
    focusTrapOptions = { clickOutsideDeactivates: true, escapeDeactivates: true },
  } = options

  // Modal stack and focus trap setup
  const modalId = ref(useId())
  const { activate, deactivate } = useFocusTrap(modalRef, focusTrapOptions)
  const { register, unregister, isTopModal } = useModalStack()

  // Register and unregister the modal to the stack
  watch(visible, (isVisible) => {
    if (isVisible) {
      register(modalId.value)
    }
    else {
      unregister(modalId.value)
    }
  }, { immediate: true })

  // Focus trap management for the modal
  const shouldTrapFocus = computed(() => isTopModal(modalId.value))
  watch(shouldTrapFocus, async (newValue) => {
    await nextTick()

    if (newValue) {
      activate()
    }
    else {
      deactivate()
    }
  }, { immediate: true })

  // Also unregister the modal when it is unmounted.
  // This should typically never happen, but will prevent potential memory leaks if it does.
  onBeforeUnmount(() => {
    unregister(modalId.value)
  })

  const isMobileDrawer = computed(() => mobileType === 'drawer')

  // Wrapper classes
  const wrapperClasses = computed(() => {
    const baseClasses = 'flex justify-center text-center focus:outline-none h-full'

    if (!isMobileDrawer.value) {
      const positionClasses = {
        center: 'items-center',
        top: 'items-start',
        bottom: 'items-end',
      }
      return [baseClasses, 'p-4', positionClasses[positionModal]]
    }

    const contentPosition = positionDrawer === 'bottom'
      ? 'items-end'
      : 'items-start'

    const paddingClasses = [
      'p-0',
      {
        'md:p-4': options.size?.value !== 'full',
      }]

    return [
      baseClasses,
      paddingClasses,
      contentPosition,
      'md:items-center',
    ]
  })

  // Content classes
  const contentClasses = computed(() => {
    const baseClasses = 'relative transform flex flex-col bg-white text-left shadow-xl w-full max-h-full transition-[max-width] duration-300 ease-in-out'

    const sizeClasses = isMobileDrawer.value
      ? {
          'sm': 'md:max-w-sm',
          'md': 'md:max-w-md',
          'lg': 'md:max-w-lg',
          'xl': 'md:max-w-xl',
          '2xl': 'md:max-w-2xl',
          '3xl': 'md:max-w-3xl',
          '4xl': 'md:max-w-4xl',
          '5xl': 'md:max-w-5xl',
          '6xl': 'md:max-w-6xl',
          'full': 'w-full h-dvh min-h-0 overflow-hidden',
        }
      : {
          'sm': 'max-w-sm',
          'md': 'max-w-md',
          'lg': 'max-w-lg',
          'xl': 'max-w-xl',
          '2xl': 'max-w-2xl',
          '3xl': 'max-w-3xl',
          '4xl': 'max-w-4xl',
          '5xl': 'max-w-5xl',
          '6xl': 'max-w-6xl',
          'full': 'w-full h-dvh min-h-0 overflow-hidden',
        }

    let borderRadiusClasses = 'rounded-xl'
    if (options.size?.value === 'full') {
      borderRadiusClasses = ''
    }
    else if (isMobileDrawer.value) {
      if (positionDrawer === 'bottom') {
        borderRadiusClasses = 'rounded-t-xl sm:rounded-xl'
      }
      else if (positionDrawer === 'top') {
        borderRadiusClasses = 'rounded-b-xl sm:rounded-xl'
      }
    }

    return [baseClasses, sizeClasses[options.size?.value ?? 'md'], borderRadiusClasses]
  })

  // Header classes
  const headerClasses = computed(() => {
    return [
      'relative p-4 pb-0 md:p-5 md:pb-0',
      {
        'shrink-0': options.size?.value === 'full',
      },
    ]
  })

  const bodyClasses = computed(() => {
    return [
      'p-4 md:p-5',
      {
        'flex flex-col flex-1 min-h-0 overflow-hidden': options.size?.value === 'full',
      },
    ]
  })

  const footerClasses = computed(() => {
    return [
      'p-4 md:p-5',
    ]
  })

  // Transition variant
  const contentTransitionVariant = computed<TransitionVariant>(() => {
    if (!isMobileDrawer.value) return 'fade-scale'
    return positionDrawer === 'bottom' ? 'modal-drawer-up' : 'modal-drawer-down'
  })

  // Animation state management
  const isVisible = ref(false)
  const isAnimating = ref(false)

  // Close trigger tracking
  const closeTrigger = ref<ModalCloseTrigger>('close')

  // Scroll lock setup
  const { lock, unlock, isDocumentLocked } = useDocumentScrollLock()

  // Watch external v-model changes
  watch(visible, (newValue) => {
    if (newValue) {
      isVisible.value = true
      isAnimating.value = false
    }
    else {
      // Start fade out animation
      isAnimating.value = true
    }
  })

  // Handle initial visibility state on mount
  onMounted(() => {
    if (visible.value) {
      isVisible.value = true
      isAnimating.value = false
      if (enableScrollLock) {
        lock()
      }
    }
  })

  // Scroll lock management
  if (enableScrollLock) {
    watch(visible, (newValue) => {
      if (newValue) {
        lock()
      }
      else {
        unlock()
      }
    })
  }

  // Handle animation completion
  function handleAfterLeave() {
    isVisible.value = false
    isAnimating.value = false
    visible.value = false // Update parent

    // Emit close event with trigger
    onClose?.(closeTrigger.value)
  }

  // Close function that starts animation
  function close(trigger: ModalCloseTrigger = 'close') {
    closeTrigger.value = trigger
    isAnimating.value = true
  }

  // Open function for manual control
  function open() {
    isVisible.value = true
    isAnimating.value = false
    visible.value = true
  }

  // Outside dismiss handlers
  function handleBackgroundClick() {
    if (!enableOutsideDismiss) return
    close('clickoutside')
  }

  /**
   * Watch for the escape key when enabled.
   */
  if (enableEscapeKey) {
    const { escape } = useMagicKeys()

    if (escape) {
      watch(escape, (newValue) => {
        if (newValue) {
          closeWithEscape()
        }
      })
    }
  }

  function closeWithExitButton() {
    close('close')
  }

  function closeWithButton() {
    close('button')
  }

  function closeWithEscape() {
    close('esc')
  }

  function closeWithOutsideClick() {
    close('clickoutside')
  }

  return {
    wrapperClasses,
    contentClasses,
    headerClasses,
    bodyClasses,
    footerClasses,
    contentTransitionVariant,
    isMobileDrawer,

    // Animation state
    isVisible,
    isAnimating,

    // Animation handlers
    handleAfterLeave,
    close,
    open,

    // Specific close methods
    closeWithButton,
    closeWithExitButton,
    closeWithEscape,
    closeWithOutsideClick,

    // Outside dismiss handlers
    handleBackgroundClick,
    enableOutsideDismiss,
    enableEscapeKey,
    enableScrollLock,
    isLocked: isDocumentLocked,

    // Focus trap and modal stack
    shouldTrapFocus,
    modalRef,
  }
}
